import { FlexColumn } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { useEffect, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useStyles } from '../../../../hooks/useStyles';
import { playbackStreamingCurrentStateState, playbackStreamingState } from '../../../../storage/streaming';
import { MinimalApiAsset } from '../../../../types/asset';
import { ChannelId } from '../../../../types/gptTypes';
import { CaptureDuration, StreamType } from '../../../../types/streaming';
import PlaybackPlayer from '../PlaybackPlayer';
import StreamingChannelTag from '../StreamingChannelTag';

type Props = Readonly<{
  duration: CaptureDuration;
}>;

function PlaybackWindow({ duration }: Props) {
  const { css, theme } = useStyles();
  const [loadedChannels, setLoadedChannels] = useState<number[]>([]);
  const [playbackStreaming, setPlaybackStreaming] = useRecoilState(playbackStreamingState);
  const playback1Ref = useRef<HTMLVideoElement>(null);
  const playback2Ref = useRef<HTMLVideoElement>(null);
  const currentState = useRecoilValue(playbackStreamingCurrentStateState);
  const videoLayerStyles = css({
    position: 'absolute',
    top: theme.sizing.scale200,
    right: theme.sizing.scale200,
    color: theme.colors.contentInversePrimary,
  });
  const asset: MinimalApiAsset | undefined =
    currentState?.asset?.subClient && currentState?.asset.client
      ? {
          assetId: currentState.asset._id,
          clientId: currentState.asset.client._id,
          subClientId: currentState.asset.subClient._id,
        }
      : undefined;

  useEffect(() => {
    if (!playbackStreaming.ident) {
      setTimeout(() => {
        setLoadedChannels([]);
      }, 0);
    }
  }, [playbackStreaming.ident]);

  const setStuck = (state: boolean, channel: ChannelId) => {
    setPlaybackStreaming((prev) => ({
      ...prev,
      frontStuck: channel === ChannelId.front ? state : prev.frontStuck,
      internalStuck: channel === ChannelId.internal ? state : prev.internalStuck,
    }));
  };

  const onBadRequest = (channel: ChannelId) => {
    setStuck(true, channel);
  };

  const onRefresh = (channel: ChannelId) => {
    setStuck(false, channel);
  };

  if (!asset || !playbackStreaming.startDate) {
    return null;
  }

  const requestedTime = playbackStreaming.startDate;

  const onLoadedPlayback = (index: number) => {
    setLoadedChannels((prev) => [...prev, index]);
  };

  return (
    <FlexColumn classNames={css({ flex: 1 })}>
      <AutoSizer>
        {({ width, height }) => {
          return (
            <FlexColumn
              gap={theme.sizing.scale600}
              classNames={css({
                width: `${width}px`,
                height: `${height}px`,
                justifyContent: 'center',
              })}
            >
              <PlaybackPlayer
                asset={asset}
                duration={duration}
                channel={ChannelId.internal}
                startDate={requestedTime}
                stuck={playbackStreaming.internalStuck}
                onBadRequest={() => onBadRequest(ChannelId.internal)}
                onRefresh={() => onRefresh(ChannelId.internal)}
                playerRef={playback1Ref}
                canLoad
                onLoadEnd={() => {
                  onLoadedPlayback(ChannelId.internal);
                }}
                streamType={StreamType.sub}
                videoLayer={
                  <Block className={videoLayerStyles}>
                    <StreamingChannelTag channelId={ChannelId.internal} />
                  </Block>
                }
                withAudio
                maxWidth={width}
              />
              <PlaybackPlayer
                asset={asset}
                duration={duration}
                channel={ChannelId.front}
                startDate={requestedTime}
                canLoad={loadedChannels.includes(ChannelId.internal)}
                stuck={playbackStreaming.frontStuck}
                onBadRequest={() => onBadRequest(ChannelId.front)}
                onRefresh={() => onRefresh(ChannelId.front)}
                playerRef={playback2Ref}
                onLoadEnd={() => {
                  onLoadedPlayback(ChannelId.front);
                }}
                videoLayer={
                  <Block className={videoLayerStyles}>
                    <StreamingChannelTag channelId={ChannelId.front} />
                  </Block>
                }
                muted
                maxWidth={width}
              />
            </FlexColumn>
          );
        }}
      </AutoSizer>
    </FlexColumn>
  );
}

export default PlaybackWindow;
